import { useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  min: number
  max: number
  /** Tuple [from, to]. `to` may equal `max` to indicate open-ended. */
  value: [number, number]
  onChange: (next: [number, number]) => void
  /** Optional label shown above the track. */
  label?: string
  /**
   * When true, the upper handle at `max` renders as "max+ år" / open-ended.
   * The parent decides what to pass to the API based on whether `to === max`.
   */
  openEndedTop?: boolean
  step?: number
}

/**
 * Two-handle range slider built from two overlaid <input type="range"> tracks.
 * No external dep. Keyboard-accessible because native range inputs already are.
 *
 * Visual: thin grey track with a primary-colored "fill" segment between the
 * two thumbs. Thumbs are styled in a global CSS file via the `.dual-range` class.
 */
export default function DualRangeSlider({
  min,
  max,
  value,
  onChange,
  label,
  openEndedTop = false,
  step = 1,
}: Props) {
  const { t } = useTranslation('bruktbil')
  const id = useId()
  const [lo, hi] = value
  const pct = (n: number) => ((n - min) / (max - min)) * 100

  const handleLo = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = Math.min(Number(e.target.value), hi)
      onChange([next, hi])
    },
    [hi, onChange],
  )

  const handleHi = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = Math.max(Number(e.target.value), lo)
      onChange([lo, next])
    },
    [lo, onChange],
  )

  const hiLabel = openEndedTop && hi === max
    ? t('units.aar', { value: `${max}+` })
    : t('units.aar', { value: hi })

  return (
    <div className="space-y-1.5">
      {label && (
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
          <span
            data-testid={`dual-range-value-${id}`}
            className="text-xs font-medium text-gray-700 dark:text-gray-200 tabular-nums"
          >
            {t('dualRange.value', { lo: t('units.aar', { value: lo }), hi: hiLabel })}
          </span>
        </div>
      )}
      <div className="relative h-6 dual-range">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-gray-200 dark:bg-dark-border" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full bg-primary-500"
          style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lo}
          onChange={handleLo}
          aria-label={t('dualRange.fra')}
          data-testid="dual-range-lo"
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none"
          style={{ zIndex: lo > max - 1 ? 5 : 3 }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={hi}
          onChange={handleHi}
          aria-label={t('dualRange.til')}
          data-testid="dual-range-hi"
          className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none"
          style={{ zIndex: 4 }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
        <span>{min}</span>
        <span>{openEndedTop ? `${max}+` : max}</span>
      </div>
    </div>
  )
}
